/**
 * Playback-delta accounting — PURE, so the overcount cases are testable
 * without a browser (ENGINE_SPEC_V2 §11).
 *
 * Extracted because the logic lived only inside LessonView, which is why the
 * floating mini-player accumulated nothing at all: there was no shared unit to
 * reuse, so the second player simply didn't implement it.
 */

/**
 * The maximum `currentTime` advance, in seconds, that can be genuine playback
 * between two `timeupdate` events.
 *
 * Browsers fire `timeupdate` roughly 4×/sec while visible and throttle to
 * ~1×/sec for a backgrounded-but-audible tab, so 1.5s comfortably admits real
 * listening while rejecting scrubs. A seek produces a jump far larger than
 * this (or negative), and is dropped.
 */
export const MAX_NATURAL_STEP_SEC = 1.5;

/**
 * Seconds of real playback between two time samples, or 0 when the step
 * cannot be natural playback.
 *
 * Rejects: backwards steps (seek back, loop), zero steps (paused ticks), and
 * forward jumps larger than MAX_NATURAL_STEP_SEC (seek forward, or a tab
 * resumed after long throttling).
 *
 * @param {number} prevTime previous currentTime, seconds
 * @param {number} nextTime current currentTime, seconds
 * @param {number} [rate=1] playbackRate — a 2× viewer advances currentTime
 *   twice as fast, so the raw delta is divided back to WALL-CLOCK seconds.
 *   Without this, watching at 2× credits double the minutes actually spent,
 *   and the weekly goal measures time spent, not content covered.
 */
export function playbackDelta(prevTime, nextTime, rate = 1) {
  const raw = nextTime - prevTime;
  if (!(raw > 0) || raw >= MAX_NATURAL_STEP_SEC) return 0;
  const r = Number(rate) > 0 ? Number(rate) : 1;
  return raw / r;
}

/**
 * Fold a time sample into an accumulator.
 * Returns the next accumulator plus whether it has reached the flush interval.
 *
 * @param {{lastTime:number, pending:number, total:number}} acc
 * @param {number} nextTime
 * @param {number} flushEverySec batching interval (§11: 30s)
 * @param {number} [rate=1]
 */
export function accumulate(acc, nextTime, flushEverySec, rate = 1) {
  const delta = playbackDelta(acc.lastTime, nextTime, rate);
  const pending = acc.pending + delta;
  return {
    lastTime: nextTime,
    pending,
    total: acc.total + delta,
    shouldFlush: pending >= flushEverySec,
  };
}

/** §11: the weekly goal batches writes every 30s of real playback. */
export const FLUSH_EVERY_SEC = 30;
